import { LabelPayerReason } from "@redotech/congo-enums/label-payer-reason";
import { Payer } from "@redotech/congo-enums/payer";
import { Provider } from "@redotech/redo-order-common/provider";
import * as z from "zod";

export const addressSchema = z.object({
  city: z.string(),
  country: z.string(),
  line1: z.string(),
  line2: z.string().optional(),
  postalCode: z.string(),
  state: z.string(),
});

/** The same amount in the currency the customer paid in (the order's presentment currency). */
export const presentmentMoneySchema = z.object({
  amount: z.string(),
  currency: z.string(),
});

export const amountSchema = z.object({
  currency: z.string(),
  amount: z.string(),
  presentment: presentmentMoneySchema.optional(),
});

export const dropoffSchema = z.object({
  shipmentGroupId: z.string(),
  provider: z.string(),
  qrCode: z.string().nullish(),
  qrCodeUrl: z.string().nullish(),
});

export type PublicDropoff = z.infer<typeof dropoffSchema>;

export const shipmentSchema = z.object({
  carrier: z.string(),
  status: z.string(),
  tracker: z.string(),
  trackingUrl: z.string(),
  postageLabel: z.string().nullish(),
  formLabel: z.string().nullish(),
  shipmentGroupId: z.string().nullish(),
  itemIds: z.array(z.string()).optional(),
  externalLocationId: z.string().nullish(),
  estimatedDeliveryDate: z.string().nullish(),
  deliveredAt: z.string().nullish(),
});

export const exchangeItemSchema = z.object({
  id: z.string(),
  variant: z.object({
    externalId: z.string().nullish(),
    name: z.string().nullish(),
  }),
  sku: z.string().nullish(),
  quantity: z.number(),
  price: z.object({
    amount: z.string(),
    tax: z.string().optional(),
    currency: z.string(),
    presentment: presentmentMoneySchema.optional(),
  }),
  originalPrice: z.object({
    amount: z.string(),
    currency: z.string(),
    presentment: presentmentMoneySchema.optional(),
  }),
  lineItemId: z.string().optional(),
});

export const assessmentsSchema = z
  .array(
    z.object({
      assignedUser: z.object({
        id: z.string(),
        name: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
      responses: z.array(
        z.object({
          type: z.string(),
          value: z.union([
            z.string(),
            z.number(),
            z.boolean(),
            z.array(z.string()),
            z.unknown(),
          ]),
        }),
      ),
    }),
  )
  .optional();

export const returnItemSchema = z.object({
  id: z.string(),
  orderItem: z.object({ id: z.string(), line_item_id: z.string() }),
  externalReturnLineItemId: z.string().nullish(),
  quantity: z.number(),
  reason: z.string().nullish(),
  reasons: z.array(z.string()),
  reasonCode: z.string().nullish(),
  reasonCodes: z.array(z.string()),
  customerComment: z.string(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  variantId: z.string().nullish(),
  productId: z.string().nullish(),
  grade: z.string().nullish(),
  outcome: z.string().nullish(),
  processingNotes: z.string().nullish(),
  processingImages: z.array(z.string()).nullish(),
  customerImages: z.array(z.string()).nullish(),
  greenReturn: z.boolean().nullish(),
  shipmentGroupIds: z.array(z.string()).nullish(),
  exchangeItem: z
    .object({
      product: z.object({
        externalId: z.string().nullish(),
        name: z.string().nullish(),
      }),
      variant: z.object({
        externalId: z.string().nullish(),
        name: z.string().nullish(),
      }),
      quantity: z.number(),
    })
    .optional(),
  refund: z.object({ amount: amountSchema, type: z.string().nullish() }),
  productValueNoTaxNoAdjustment: z.object({ amount: amountSchema }),
  status: z.string(),
  productAdjustment: z.string().nullish(),
  multipleChoiceQuestions: z
    .array(z.object({ question: z.string(), answer: z.string() }))
    .nullish(),
  assessments: assessmentsSchema,
  externalIdentifier: z.string().nullish(),
});

export const giftCardSchema = z.object({
  code: z.string().nullish(),
  amount: z.object({ currency: z.string(), amount: z.number().nullish() }),
  externalId: z.string().nullish(),
});

export const refundSchema = z.object({
  amount: z.object({ currency: z.string(), amount: z.number() }),
  externalId: z.string().nullish(),
});

export const noteSchema = z.object({
  message: z.string().nullish(),
  image: z.string().nullish(),
});

export const tagSchema = z.object({ name: z.string(), source: z.string() });

export const integrationSchema = z.object({
  provider: z.string(),
  type: z.string(),
  externalId: z.string(),
});

const orderMoneySchema = z.object({
  amount: z.string().optional(),
  currency: z.string().optional(),
  presentment: presentmentMoneySchema.optional(),
});

export const publicOrderItemSchema = z.object({
  id: z.string(),
  externalId: z.string().optional(),
  quantity: z.number(),
  product: z.object({ externalId: z.string().optional(), name: z.string() }),
  variant: z.object({
    externalId: z.string().optional(),
    name: z.string(),
    weight: z.object({ kg: z.number().optional() }).optional(),
    sku: z.string().optional(),
  }),
  price: orderMoneySchema,
  fulfillmentLocationId: z.string().nullable(),
});

export const orderProtectionSchema = z.object({
  covered: z.boolean(),
  returnsCoverage: z.boolean(),
  packageProtection: z.boolean(),
  finalSaleCoverage: z.boolean(),
});

export type PublicOrderProtection = z.infer<typeof orderProtectionSchema>;

export const returnProtectionSchema = orderProtectionSchema.extend({
  labelPayer: z.enum(Payer).nullable(),
  labelPayerReason: z.enum(LabelPayerReason).nullable(),
});

export type PublicReturnProtection = z.infer<typeof returnProtectionSchema>;

export const publicOrderResponseSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  externalId: z.string().nullish(),
  // Ecommerce provider's order-creation timestamp (ISO 8601), e.g. Shopify
  // `created_at` — when the customer placed the order, not when Redo ingested it.
  placedAt: z.string().nullish(),
  customer: z.object({
    name: z.object({ given: z.string(), surname: z.string() }),
    emailAddress: z.string().optional(),
    phoneNumber: z.string().optional(),
  }),
  items: z.array(publicOrderItemSchema),
  total: orderMoneySchema,
  discounts: orderMoneySchema,
  lineItemsTotal: orderMoneySchema,
  shippingCost: orderMoneySchema,
  taxes: orderMoneySchema,
  protection: orderProtectionSchema,
});

export type PublicOrderResponse = z.infer<typeof publicOrderResponseSchema>;

const externalReturnIdSchema = z.object({
  provider: z.literal(Provider.SHOPIFY),
  value: z.string(),
});

export const publicReturnResponseSchema_ = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  type: z.string(),
  status: z.string(),
  completeWithNoAction: z.boolean(),
  compensationMethods: z.array(z.string()),
  externalReturnIds: z.array(externalReturnIdSchema),
  externalOrderIds: z.array(z.string()),
  shopifyOrderIds: z.array(z.string()),
  order: z.object({
    id: z.string(),
    name: z.string().nullish(),
    externalId: z.string().optional(),
  }),
  destination: z.object({
    mailingAddress: addressSchema,
    phoneNumber: z.string().nullish(),
  }),
  source: z.object({
    emailAddress: z.string().nullish(),
    mailingAddress: addressSchema.nullish(),
    name: z.object({ given: z.string(), surname: z.string() }),
    phoneNumber: z.string().nullish(),
  }),
  items: z.array(returnItemSchema),
  internalCreatedByName: z.string().nullish(),
  giftCards: z.array(giftCardSchema),
  refunds: z.array(refundSchema),
  totals: z.object({
    refund: z.object({ amount: amountSchema }),
    exchange: z.object({ amount: amountSchema }),
    storeCredit: z.object({ amount: amountSchema }),
    charge: z.object({ amount: amountSchema }),
    labelCost: z.object({ amount: amountSchema }),
    shippingFee: z.object({ amount: amountSchema }),
    originalShippingRefund: z.object({ amount: amountSchema }),
  }),
  exchange: z.object({
    order: z.object({ externalId: z.string().nullable() }),
    items: z.array(exchangeItemSchema),
    totalTax: z.object({
      amount: z.string(),
      currency: z.string(),
      presentment: presentmentMoneySchema.optional(),
    }),
    itemCount: z.number(),
    provision: z.string().optional(),
  }),
  notes: z.array(noteSchema),
  tags: z.array(tagSchema),
  shipment: shipmentSchema.optional(),
  shipments: z.array(shipmentSchema),
  dropoffs: z.array(dropoffSchema),
  integrations: z.array(integrationSchema),
  returnsCenterId: z.string().nullish(),
  protection: returnProtectionSchema,
});

export const publicReturnResponseSchema: z.ZodType<PublicReturnResponse> =
  publicReturnResponseSchema_;
export type PublicReturnResponse = z.infer<typeof publicReturnResponseSchema_>;
export type ExchangeItem = z.infer<typeof exchangeItemSchema>;
export type PublicReturnShipment = z.infer<typeof shipmentSchema>;

export const listReturnsInputSchema = z.object({
  storeId: z.string().describe("The store ID to list returns for"),
  pageContinue: z
    .string()
    .optional()
    .describe(
      "Pagination cursor from a previous response's pageNext field. Omit for the first page.",
    ),
  pageSize: z
    .number()
    .optional()
    .describe("Number of returns per page. Defaults to 10."),
  updatedAtMin: z
    .string()
    .optional()
    .describe(
      "Filter to returns updated after this date (ISO 8601, e.g. '2025-01-01T00:00:00Z')",
    ),
  updatedAtMax: z
    .string()
    .optional()
    .describe(
      "Filter to returns updated before this date (ISO 8601, e.g. '2025-12-31T23:59:59Z')",
    ),
  shopifyOrderName: z
    .string()
    .optional()
    .describe(
      "Filter by Shopify order name (e.g. '#1234'). Use when looking up returns for a specific order.",
    ),
  providerOrderName: z
    .string()
    .optional()
    .describe("Filter by provider order name"),
  email: z
    .string()
    .optional()
    .describe(
      "Filter by customer email address. Use when looking up returns for a specific customer.",
    ),
  status: z
    .string()
    .optional()
    .describe(
      "Filter by return status. Valid values: open, in_transit, delivered, needs_review, in_review, complete, rejected, flagged, pre-shipment.",
    ),
});
export type ListReturnsInput = z.infer<typeof listReturnsInputSchema>;

export const updateReturnStatusInputSchema = z.object({
  status: z
    .string()
    .describe(
      "The new status for the return. Valid values: open, in_transit, delivered, needs_review, in_review, complete, rejected, flagged, pre-shipment.",
    ),
});
export type UpdateReturnStatusInput = z.infer<
  typeof updateReturnStatusInputSchema
>;

export const createReturnCommentInputSchema = z.object({
  message: z
    .string()
    .describe(
      "The comment message to add to the return timeline. For disposition notes, use the format '{product_title} - {variant_title} disposition: {grade}' to automatically update the product's grade.",
    ),
  image: z
    .string()
    .optional()
    .describe("URL of an image to attach to the comment"),
});
export type CreateReturnCommentInput = z.infer<
  typeof createReturnCommentInputSchema
>;
